import {asIssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {useEffect, useMemo} from 'react'

import type {IssueFieldWithUnsavedValue} from '../../../utils/issue-field-helper'
import {toIssueFieldWithUnsavedValue} from '../../../utils/issue-field-helper'

export interface UsePinnedFieldsStateOptions {
  issueType: IssueTypePickerIssueType | null
  setFields: React.Dispatch<React.SetStateAction<IssueFieldWithUnsavedValue[]>>
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
}

export interface UsePinnedFieldsStateResult {
  pinnedFieldIds: Set<string>
}

/**
 * Custom hook that manages pinned fields state for issue types.
 * Handles filtering, mapping, and auto-population of pinned fields.
 */
export function usePinnedFieldsState({
  issueType,
  setFields,
  setFieldsSet,
}: UsePinnedFieldsStateOptions): UsePinnedFieldsStateResult {
  const pinnedFields = useMemo(() => {
    return (
      (issueType?.pinnedFields || [])
        // Relay generates a '%other' __typename as a fallback for union members not known at compile time.
        // We filter out '%other' to exclude unknown or unsupported field types from the list.
        .filter(field => field.__typename !== '%other')
        .map(field => toIssueFieldWithUnsavedValue(asIssueFieldSelection(field), null))
    )
  }, [issueType])

  const pinnedFieldIds = useMemo(() => new Set(pinnedFields.map(f => f.field.id!)), [pinnedFields])

  useEffect(() => {
    if (issueType && pinnedFields.length > 0) {
      // Auto-populate pinned fields when issue type changes
      setFields(prevFields => {
        // Create pinned fields, preserving existing values if they exist
        const fieldsWithValues = pinnedFields.map(pinnedField => {
          const existingField = prevFields.find(f => f.field.id === pinnedField.field.id)
          return existingField && existingField.value ? existingField : pinnedField
        })

        // Keep user-added fields that have values and are not pinned
        const userFields = prevFields.filter(f => !pinnedFieldIds.has(f.field.id!) && f.value)
        return [...fieldsWithValues, ...userFields]
      })
      setFieldsSet(pinnedFields.map(field => field.field.name!))
    } else {
      // Here we only need the manually added fields without the pinned ones
      setFields(prevFields => prevFields.filter(f => !pinnedFieldIds.has(f.field.id!)))
      setFieldsSet(prevFields => prevFields.filter(name => !pinnedFields.map(f => f.field.name).includes(name)))
    }
  }, [issueType, pinnedFieldIds, pinnedFields, setFields, setFieldsSet])

  return {
    pinnedFieldIds,
  }
}
